<!--
  Camera Analysis — XYLO VISION · EW1, the camera above the log conveyor (ex-XYLO-EWANN).

  Per day: logs IN (counted live by the camera as they pass) and logs OUT (measured
  afterwards by the deferred computation: length, diameter, volume). The data stays
  in the EW1 database (other VPS): /api/ewann/sessions relays it read-only, one row
  per conveyor session, and this screen groups the rows by day or by month.

  Reached from Production → list ("Camera analysis" button). Barcodes scanned with
  XYLO PRD are on the other page (ProductionAnalysis.vue, "Barcode analysis").
-->
<template>
  <div id="camera-analysis">
    <div class="vx-row">
      <div class="vx-col w-full">

        <div class="flex flex-wrap items-end justify-end mb-4">
          <div class="mr-3">
            <label class="filter-label">From</label>
            <input type="date" v-model="from" class="date-input" @change="load" />
          </div>
          <div class="mr-3">
            <label class="filter-label">To</label>
            <input type="date" v-model="to" class="date-input" @change="load" />
          </div>
          <vs-button
            class="mr-2"
            type="border"
            color="primary"
            size="small"
            @click="clearRange"
            :disabled="!from && !to"
          >
            All time
          </vs-button>
          <vs-button
            type="border"
            color="primary"
            size="small"
            icon-pack="feather"
            icon="icon-refresh-cw"
            @click="load"
          >
            {{ loading ? 'Loading…' : 'Refresh' }}
          </vs-button>
        </div>

        <vx-card class="mb-base">
          <div class="flex flex-wrap items-start justify-between mb-6">
            <div>
              <h3 class="mb-1">XYLO VISION · EW1 — Logs in / out</h3>
              <p class="src-note mb-0">
                Logs that went past the conveyor camera (in) and logs it measured (out), with their volume.
              </p>
            </div>
            <vs-button
              type="border"
              color="primary"
              size="small"
              icon-pack="feather"
              icon="icon-external-link"
              target
              :href="ew1Url"
            >
              Open EW1
            </vs-button>
          </div>

          <div class="flex flex-wrap items-center mb-4">
            <vs-button
              v-for="g in groups"
              :key="g.key"
              class="mr-2"
              size="small"
              :type="group === g.key ? 'filled' : 'border'"
              color="primary"
              @click="setGroup(g.key)"
            >
              {{ g.label }}
            </vs-button>
          </div>

          <div v-if="truncated" class="warn-note mb-4">
            Only the {{ cap }} most recent sessions are listed — narrow the date range to see older ones.
          </div>

          <div v-if="loading" class="text-center py-12 text-muted">Loading camera data…</div>

          <div v-else-if="error" class="empty-state">
            <feather-icon icon="AlertTriangleIcon" svgClasses="w-6 h-6" class="mb-2" />
            <div class="font-medium">Could not load XYLO VISION EW1</div>
            <div class="text-sm">{{ error }}</div>
          </div>

          <div v-else-if="!periods.length" class="empty-state">
            <feather-icon icon="InboxIcon" svgClasses="w-6 h-6" class="mb-2" />
            <div class="font-medium">No log seen by the camera for this period</div>
            <div class="text-sm">Sessions appear here once the conveyor stops and the camera closes the session.</div>
          </div>

          <!-- One block per period; in the day view a click unfolds its sessions. -->
          <div v-else class="table-scroll">
            <table class="pivot">
              <thead>
                <tr>
                  <th class="col-label">{{ group === 'month' ? 'Month' : 'Date' }}{{ group === 'day' ? ' / Session' : '' }}</th>
                  <th class="col-label">Status</th>
                  <th class="num">Logs in</th>
                  <th class="num">Logs out</th>
                  <th class="num col-total">Volume out</th>
                </tr>
              </thead>
              <tbody v-for="p in periods" :key="p.key">
                <tr class="period-row" :class="group === 'day' ? 'clickable' : ''" @click="toggle(p.key)">
                  <td class="col-label">
                    <feather-icon
                      v-if="group === 'day'"
                      :icon="open[p.key] ? 'ChevronDownIcon' : 'ChevronRightIcon'"
                      svgClasses="w-4 h-4"
                      class="align-middle mr-1"
                    />
                    {{ p.label }}
                    <span class="count-pill">{{ p.sessions.length }} {{ p.sessions.length === 1 ? 'session' : 'sessions' }}</span>
                  </td>
                  <td class="col-label">
                    <span v-if="p.waiting" class="state-pill wait">{{ p.waiting }} not measured yet</span>
                  </td>
                  <td class="num">{{ formatInt(p.counted) }}</td>
                  <td class="num">{{ formatInt(p.measured) }}</td>
                  <td class="num col-total">{{ formatM3(p.volume) }}</td>
                </tr>
                <template v-if="group === 'day' && open[p.key]">
                  <tr v-for="s in p.sessions" :key="s.id">
                    <td class="col-label op-label">#{{ s.session_no || '?' }} · {{ s.start || '—' }} → {{ s.end || '—' }}</td>
                    <td class="col-label"><span class="state-pill" :class="stateOf(s).cls">{{ stateOf(s).label }}</span></td>
                    <td class="num">{{ formatInt(s.counted) }}</td>
                    <td class="num">{{ s.status === 'done' ? formatInt(s.measured) : '—' }}</td>
                    <td class="num col-total">{{ s.status === 'done' ? formatM3(s.volume_m3) : '—' }}</td>
                  </tr>
                </template>
              </tbody>
              <tfoot>
                <tr>
                  <td class="col-label">
                    TOTAL
                    <span class="count-pill">{{ rows.length }} {{ rows.length === 1 ? 'session' : 'sessions' }}</span>
                  </td>
                  <td class="col-label"></td>
                  <td class="num">{{ formatInt(total.counted) }}</td>
                  <td class="num">{{ formatInt(total.measured) }}</td>
                  <td class="num col-total">{{ formatM3(total.volume) }}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <p v-if="periods.length" class="src-note mt-4">
            <b>In</b> = logs counted live by the camera as they pass under it.
            <b>Out</b> = logs measured afterwards (length, diameter, volume) — this computation runs after
            each session, so the latest sessions can still be waiting. A session in progress appears once it
            closes. Days and times are site time ({{ tz }}). Volumes are still being calibrated against
            manual measurements.
          </p>
        </vx-card>

      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/axios.js'

// EW1 job status → label shown on a session line
const STATES = {
  done:     { label: 'Measured',   cls: 'ok' },
  running:  { label: 'Computing',  cls: 'wait' },
  pending:  { label: 'Waiting',    cls: 'wait' },
  failed:   { label: 'Failed',     cls: 'bad' },
  canceled: { label: 'Canceled',   cls: 'bad' },
  none:     { label: 'Not queued', cls: 'bad' }
}

export default {
  name: 'CameraAnalysis',
  data () {
    return {
      loading: false,
      error: '',
      from: '',
      to: '',
      group: 'day',
      // One row per conveyor session (EW1 API order: most recent first)
      rows: [],
      tz: 'Europe/Bucharest',
      truncated: false,
      cap: 0,
      open: {},            // day → sessions unfolded?
      ew1Url: 'https://xylo-vision-ew1.codesinn.com',
      groups: [
        { key: 'day',   label: 'By day' },
        { key: 'month', label: 'By month' }
      ]
    }
  },
  computed: {
    // Periods most recent first, sessions most recent first inside.
    periods () {
      const byKey = {}
      const out = []
      this.rows.forEach((s) => {
        const key = this.group === 'month' ? s.day.slice(0, 7) : s.day
        if (!byKey[key]) {
          byKey[key] = { key, label: this.periodLabel(key), sessions: [], counted: 0, measured: 0, volume: 0, waiting: 0 }
          out.push(byKey[key])
        }
        const p = byKey[key]
        p.sessions.push(s)
        p.counted += Number(s.counted) || 0
        if (s.status === 'done') {
          p.measured += Number(s.measured) || 0
          p.volume += Number(s.volume_m3) || 0
        } else {
          p.waiting += 1
        }
      })
      return out
    },
    total () {
      return this.periods.reduce((t, p) => ({
        counted: t.counted + p.counted,
        measured: t.measured + p.measured,
        volume: t.volume + p.volume
      }), { counted: 0, measured: 0, volume: 0 })
    }
  },
  created () {
    this.load()
  },
  methods: {
    setGroup (g) {
      if (this.group === g) return
      this.group = g
      this.resetOpen()
    },
    toggle (key) {
      if (this.group !== 'day') return
      this.$set(this.open, key, !this.open[key])
    },
    // Most recent day unfolded, the rest folded.
    resetOpen () {
      this.open = {}
      if (this.group === 'day' && this.periods.length) this.$set(this.open, this.periods[0].key, true)
    },
    clearRange () {
      this.from = ''
      this.to = ''
      this.load()
    },
    load () {
      this.loading = true
      this.error = ''
      const params = {}
      if (this.from) params.from = this.from
      if (this.to) params.to = this.to
      axios.get('/ewann/sessions', { params })
        .then((res) => {
          const d = res.data || {}
          this.rows = d.rows || []
          this.tz = d.tz || this.tz
          this.truncated = !!d.truncated
          this.cap = d.cap || 0
        })
        .catch((err) => {
          console.error('Error loading camera analysis:', err)
          this.error = (err.response && err.response.data && err.response.data.message) || 'XYLO VISION EW1 did not answer.'
          this.rows = []
          this.truncated = false
        })
        .finally(() => {
          this.loading = false
          this.resetOpen()
        })
    },
    stateOf (s) {
      return STATES[s.status] || { label: s.status, cls: 'bad' }
    },
    // '2026-08-04' → '04/08/2026' ; '2026-08' → 'August 2026'
    periodLabel (period) {
      const parts = period.split('-')
      if (parts.length === 2) {
        const d = new Date(Number(parts[0]), Number(parts[1]) - 1, 1)
        return isNaN(d) ? period : d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
      }
      return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : period
    },
    formatInt (n) {
      if (n === null || n === undefined || isNaN(n)) return '—'
      return new Intl.NumberFormat('en-GB').format(Number(n))
    },
    formatM3 (n) {
      if (n === null || n === undefined || isNaN(n)) return '—'
      return `${new Intl.NumberFormat('en-GB', {
        minimumFractionDigits: 3,
        maximumFractionDigits: 3
      }).format(Number(n))} m³`
    }
  }
}
</script>

<style scoped>
.text-muted { color: #6c757d; }

.src-note { font-size: 0.78rem; color: #94a3b8; max-width: 640px; }
.filter-label { display: block; font-size: 0.7rem; color: #94a3b8; margin-bottom: 3px; }
.date-input {
  border: 1px solid #dae1e7; border-radius: 5px; padding: 6px 9px;
  font-size: 0.85rem; color: #1e293b; background: #fff;
}

/* Same table as Barcode analysis: period line, sessions indented under it */
.table-scroll { overflow-x: auto; }
.pivot { width: 100%; border-collapse: collapse; font-size: 0.86rem; }
.pivot th, .pivot td { padding: 9px 14px; border-bottom: 1px solid #eef2f7; white-space: nowrap; }
.pivot thead th {
  font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: .04em;
  color: #64748b; background: #f8fafc; border-bottom: 1px solid #e2e8f0;
}
.pivot tbody tr:hover td { background: #f8fafc; }
.pivot .num { text-align: right; }
.pivot .col-label { text-align: left; color: #1e293b; }
.pivot .col-total { font-weight: 600; color: #1e293b; width: 180px; }
.pivot .period-row td {
  background: #f8fafc; font-weight: 700; border-top: 1px solid #e2e8f0;
}
.pivot .period-row .col-total { color: #7367f0; }
.pivot .op-label { padding-left: 34px; color: #475569; }
.pivot tfoot td {
  border-top: 2px solid #cbd5e1; background: #f1f5f9; font-weight: 700; color: #1e293b;
}
.pivot .clickable { cursor: pointer; user-select: none; }

.count-pill {
  display: inline-block; margin-left: 10px; padding: 1px 9px; border-radius: 999px;
  background: #eef2ff; color: #4f46e5; font-size: 0.7rem; font-weight: 600;
}
.state-pill {
  display: inline-block; padding: 1px 9px; border-radius: 999px;
  font-size: 0.7rem; font-weight: 600;
}
.state-pill.ok   { background: #ecfdf5; color: #047857; }
.state-pill.wait { background: #fff7ed; color: #9a3412; }
.state-pill.bad  { background: #fef2f2; color: #b91c1c; }
.warn-note {
  background: #fff7ed; color: #9a3412; border: 1px solid #fed7aa;
  border-radius: 6px; padding: 8px 12px; font-size: 0.8rem;
}

.empty-state { text-align: center; color: #94a3b8; padding: 40px 10px; }
</style>
